import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useThemeContext } from '../../context/ThemeContext';
import { useData } from '../../context/DataContext';

import digitalIndiaLogo from '../../assets/digital-india.png';
import swachhBharatLogo from '../../assets/swachh-bharat.png';

const logoFallbacks: Record<string, string> = {
    'Digital India': digitalIndiaLogo,
    'Swachh Bharat': swachhBharatLogo
};

const FooterLogoCarousel: React.FC = () => {
    const { isDarkMode } = useThemeContext();
    const { governmentPortals } = useData(); // New line
    const [currentIndex, setCurrentIndex] = useState(0); // New line

    // Fallback logos if empty
    const portals = governmentPortals.length > 0 ? governmentPortals : [ // New lines
        { id: 'digital_india', name: 'Digital India', url: 'https://digitalindia.gov.in/', logo: 'https://www.digitalindia.gov.in/assets/images/logo.png' },
        { id: 'swachh_bharat', name: 'Swachh Bharat', url: 'https://swachhbharatmission.gov.in/', logo: 'https://swachhbharatmission.gov.in/images/swachh_bharat_logo.png' }
    ]; // End new lines

    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const checkScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const scrollAmount = 200;
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
            setTimeout(checkScroll, 300);
        }
    };

    return (
        <div className={`py-6 ${isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50'} border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
            <div className="max-w-7xl mx-auto px-4 relative">
                {canScrollLeft && (
                    <button
                        onClick={() => scroll('left')}
                        className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full shadow-md ${isDarkMode ? 'bg-slate-700 text-white' : 'bg-white text-gray-700'}`}
                        aria-label="Scroll left"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                )}

                <div
                    ref={scrollRef}
                    className="flex items-center gap-8 overflow-x-auto scrollbar-hide py-2 px-8 scroll-smooth"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {portals.map((portal) => (
                        <a
                            key={portal.id}
                            href={portal.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-col items-center gap-2 min-w-[100px] shrink-0"
                        >
                            <div className={`h-12 md:h-14 flex items-center justify-center px-4 py-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-white hover:shadow-md'}`}>
                                <img
                                    src={logoFallbacks[portal.name] || portal.logo || ''}
                                    alt={portal.name}
                                    className="h-8 md:h-10 w-auto object-contain grayscale group-hover:grayscale-0 transition-all"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                    }}
                                />
                            </div>
                            <span className={`text-xs font-medium text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} group-hover:text-primary transition-colors`}>
                                {portal.name}
                            </span>
                        </a>
                    ))}
                </div>

                {canScrollRight && (
                    <button
                        onClick={() => scroll('right')}
                        className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full shadow-md ${isDarkMode ? 'bg-slate-700 text-white' : 'bg-white text-gray-700'}`}
                        aria-label="Scroll right"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                )}
            </div>
        </div >
    );
};

export default FooterLogoCarousel;
